import { dashboardApi } from "./dashboardApi";
import type { Pipeline } from "@utils/types/integration";

/** Nested atoms from `GET company-portal/processing/entities/`. */
export interface PortalLabRecentPortalDemoAtoms {
    deployment_url?: string | null;
    /** Preferred progress key from entities list. */
    state?: string | null;
    /** Legacy alias — prefer `state` when both are present. */
    stage?: string | null;
    /** Connection platform, e.g. `thoughtspot` | `databricks`. */
    flow_type?: string | null;
}

export interface PortalLabRecentPortal {
    id: number;
    name: string;
    description: string;
    url: string;
    logo: string;
    published_url?: string | null;
    created_date?: string | null;
    edited_date?: string | null;
    demo_title?: string | null;
    demo_atoms?: PortalLabRecentPortalDemoAtoms | null;
}

export type PortalLabCompanyPortalStateResponse = Record<string, unknown>;
export type PortalLabCompanyPortalStateId = number | string;

export const COMPANY_PORTAL_STAGE_STEP1_PROCESSING = "Step 1 Processing";
export const COMPANY_PORTAL_STAGE_STEP1_FINISHED = "Step 1 Finished";
export const COMPANY_PORTAL_STAGE_STEP1_FAILED = "Step 1 Failed";
export const COMPANY_PORTAL_STAGE_STEP2_PROCESSING = "Step 2 Processing";
export const COMPANY_PORTAL_STAGE_STEP2_FAILED = "Step 2 Failed";
export const COMPANY_PORTAL_STAGE_STEP2_FINISHED = "Step 2 Finished";
export const COMPANY_PORTAL_STAGE_STEP3_PROCESSING = "Step 3 Processing";
export const COMPANY_PORTAL_STAGE_STEP3_FAILED = "Step 3 Failed";
export const COMPANY_PORTAL_STAGE_STEP3_FINISHED = "Step 3 Finished";
export const COMPANY_PORTAL_STAGE_BUILD_PROCESSING = "Build Processing";
export const COMPANY_PORTAL_STAGE_BUILD_FAILED = "Build Failed";
export const COMPANY_PORTAL_STAGE_BUILD_FINISHED = "Build Finished";
export const COMPANY_PORTAL_STAGE_PORTAL_READY = "Portal Ready";
export const COMPANY_PORTAL_STAGE_DEPLOYMENT_PROCESSING = "Deployment Processing";
export const COMPANY_PORTAL_STAGE_DEPLOYMENT_FAILED = "Deployment Failed";
export const COMPANY_PORTAL_STAGE_DEPLOYMENT_FINISHED = "Deployment Finished";

export interface PortalLabCompanyPortalStageResponse {
    stage: string;
    status: string;
    deployment_url: string | null;
}

export interface PortalLabGeneratedTemplateChatMessage {
    role: string;
    content: string;
}

export interface PortalLabCompanyPortalProcessingTemplateResponse {
    html_content: string;
    chat_history: PortalLabGeneratedTemplateChatMessage[];
}

export interface PortalLabUpdateCompanyPortalStateArgs {
    processingId: PortalLabCompanyPortalStateId;
    body: Record<string, unknown>;
}

export interface PortalLabCompanyPortalProcessingStep1Args {
    website_url: string;
    pipeline_id: number;
    /** Demo Lab processing entity id when starting from Demo Lab final screen. */
    demolab_id?: number | string | null;
    /** Demo Lab display title when starting from Demo Lab final screen. */
    demo_title?: string | null;
    step?: number;
}

export interface PortalLabCompanyPortalProcessingStep2Args {
    processingId: PortalLabCompanyPortalStateId;
    body: Record<string, unknown>;
}

export interface PortalLabCompanyPortalCopilotArgs {
    processingId: PortalLabCompanyPortalStateId;
    prompt: string;
}

export interface PortalLabCompanyPortalCopilotResponse {
    ai_message: string;
    applied: boolean;
    moodboard: Record<string, unknown>;
    theme: Record<string, unknown>;
    chat_history: { role: string; content: string }[];
}

export interface PortalLabCompanyPortalGenerateTemplateArgs {
    processingId: PortalLabCompanyPortalStateId;
}

export type PortalLabCompanyPortalProcessingUploadKind = "logo" | "banner" | "image";

export interface PortalLabCompanyPortalProcessingUploadArgs {
    file: File;
    /** Asset category expected by `company-portal/processing/upload/`. */
    type: PortalLabCompanyPortalProcessingUploadKind;
}

/** Adjust fields once the upload endpoint contract is finalized (e.g. `url`, nested payload). */
export type PortalLabCompanyPortalProcessingUploadResponse = Record<string, unknown>;

export interface PortalLabExtractBrandKitArgs {
    website: string;
}

export interface PortalLabExtractBrandKitResponse {
    colors: Record<string, string>;
}

export interface PortalLabUploadCompanyPortalLogoArgs {
    processingId: PortalLabCompanyPortalStateId;
    file: File;
}

export interface PortalLabUploadCompanyPortalLogoResponse {
    logo_url: string;
}

export interface PortalLabPageTemplateElement {
    id: string;
    type: string;
    span: number;
    config: Record<string, unknown>;
}

export interface PortalLabPageTemplateRow {
    id: string;
    elements: PortalLabPageTemplateElement[];
}

export interface PortalLabPageTemplate {
    id: string;
    name: string;
    description: string;
    rows: PortalLabPageTemplateRow[];
}

export type PortalLabPageTemplatesResponse = PortalLabPageTemplate[];

export interface PortalLabColorThemeColors {
    primary?: string | null;
    secondary?: string | null;
    dark?: string | null;
    light?: string | null;
    accent?: string | null;
}

export interface PortalLabColorTheme {
    id: string;
    colors: PortalLabColorThemeColors;
}

export type PortalLabColorThemesResponse = PortalLabColorTheme[];

export interface PortalLabThoughtspotLiveboardTilesByPipelineArgs {
    pipeline_id: number;
    filter?: string;
}

export interface PortalLabThoughtspotLiveboardTileItem {
    id?: string | null;
    name?: string | null;
    type?: string | null;
}

export interface PortalLabThoughtspotLiveboardTilesByPipelineResponse {
    status?: string;
    message?: string;
    /** KPI / chart list from `demo-lab/thoughtspot-liveboard-tiles/` (connection + liveboard). */
    kpis?: PortalLabThoughtspotLiveboardTileItem[];
    /** Chart list for specific-chart UI (same endpoint; alternate response shape). */
    charts?: PortalLabThoughtspotLiveboardTileItem[];
    /** Chart/tile list (alternate or legacy API shape). */
    tiles?: PortalLabThoughtspotLiveboardTileItem[];
    /** Liveboard list from ThoughtSpot (`id`, `name` per item). */
    liveboards?: PortalLabThoughtspotLiveboardTileItem[];
}

export interface PortalLabThoughtspotLiveboardTilesByConnectionArgs {
    connection_id: number;
    liveboard_id: string;
}

/** Response for tiles under a specific liveboard + ThoughtSpot connection. */
export type PortalLabThoughtspotLiveboardTilesByConnectionResponse =
    PortalLabThoughtspotLiveboardTilesByPipelineResponse;

export interface PortalLabThoughtspotSpotterModelsArgs {
    pipeline_id: number;
}

/** Response from `demo-lab/thoughtspot-spotter-models/` (ThoughtSpot logical tables as Spotter models). */
export interface PortalLabThoughtspotSpotterModelsResponse {
    status?: string;
    message?: string;
    models?: PortalLabThoughtspotLiveboardTileItem[];
}

export interface PortalLabDatabricksDashboardsArgs {
    pipeline_id: number;
    filter?: string;
}

export interface PortalLabDatabricksDashboardWidgetsArgs {
    pipeline_id: number;
    dashboard_id: string;
}

export type PortalLabDatabricksDashboardItem = PortalLabThoughtspotLiveboardTileItem & {
    dashboard_id?: string | null;
    dashboard_name?: string | null;
    display_name?: string | null;
    label?: string | null;
};

export interface PortalLabDatabricksDashboardsResponse {
    status?: string;
    message?: string;
    dashboards?: PortalLabDatabricksDashboardItem[];
    results?: PortalLabDatabricksDashboardItem[];
}

export interface PortalLabDatabricksDashboardWidgetsResponse {
    status?: string;
    message?: string;
    kpis?: PortalLabDatabricksDashboardItem[];
    charts?: PortalLabDatabricksDashboardItem[];
    spaces?: PortalLabDatabricksDashboardItem[];
    genie_spaces?: PortalLabDatabricksDashboardItem[];
    models?: PortalLabDatabricksDashboardItem[];
    widgets?: PortalLabDatabricksDashboardItem[];
    tiles?: PortalLabDatabricksDashboardItem[];
    results?: PortalLabDatabricksDashboardItem[];
}

const portalLabEndpoints = dashboardApi.injectEndpoints({
    endpoints: (builder) => ({
        extractPortalBrandKit: builder.mutation<
            PortalLabExtractBrandKitResponse,
            PortalLabExtractBrandKitArgs
        >({
            query: ({ website }) => ({
                url: "demo-lab/company-portal/brand-kit/extract/",
                method: "POST",
                body: { website },
            }),
        }),
        uploadCompanyPortalLogo: builder.mutation<
            PortalLabUploadCompanyPortalLogoResponse,
            PortalLabUploadCompanyPortalLogoArgs
        >({
            query: ({ processingId, file }) => {
                const body = new FormData();
                body.append("logo", file);
                return {
                    url: `demo-lab/${processingId}/company-portal/logo/`,
                    method: "POST",
                    body,
                };
            },
        }),
        getPortalLabRecentPortals: builder.query<PortalLabRecentPortal[], void>({
            query: () => ({
                url: "/company-portal/processing/entities/",
                method: "GET",
            }),
        }),
        deletePortalLabEntity: builder.mutation<void, PortalLabCompanyPortalStateId>({
            query: (processingId) => ({
                url: `company-portal/processing/${processingId}/`,
                method: "DELETE",
            }),
        }),
        getPortalLabPageTemplates: builder.query<PortalLabPageTemplatesResponse, void>({
            query: () => ({
                url: "demo-lab/company-portal/page-templates/",
                method: "GET",
            }),
        }),
        getPortalLabCompanyPortalColorThemes: builder.query<PortalLabColorThemesResponse, void>({
            query: () => ({
                url: "company-portal/processing/color-themes/",
                method: "GET",
            }),
        }),
        getPortalLabPipelinesList: builder.query<Pipeline[], void>({
            query: () => ({
                url: "integrations/pipelines/",
                method: "GET",
            }),
        }),
        getPortalLabCompanyPortalState: builder.query<
            PortalLabCompanyPortalStateResponse,
            PortalLabCompanyPortalStateId
        >({
            query: (processingId) => ({
                url: `company-portal/processing/${processingId}/`,
                method: "GET",
            }),
        }),
        getPortalLabCompanyPortalProcessingStage: builder.query<
            PortalLabCompanyPortalStageResponse,
            PortalLabCompanyPortalStateId
        >({
            query: (processingId) => ({
                url: `company-portal/processing/${processingId}/stage/`,
                method: "GET",
            }),
        }),
        getPortalLabCompanyPortalProcessingTemplate: builder.query<
            PortalLabCompanyPortalProcessingTemplateResponse,
            PortalLabCompanyPortalStateId
        >({
            query: (processingId) => ({
                url: `company-portal/processing/${processingId}/template/`,
                method: "GET",
            }),
        }),
        postPortalLabCompanyPortalProcessingStep1: builder.mutation<
            PortalLabCompanyPortalStateResponse,
            PortalLabCompanyPortalProcessingStep1Args
        >({
            query: ({ website_url, pipeline_id, demolab_id, demo_title, step = 1 }) => {
                const trimmedDemoTitle =
                    typeof demo_title === "string" ? demo_title.trim() : "";

                return {
                    url: `company-portal/processing/step-${step}/`,
                    method: "POST",
                    body: {
                        website_url,
                        pipeline_id,
                        ...(demolab_id != null && demolab_id !== ""
                            ? { demolab_id }
                            : {}),
                        ...(trimmedDemoTitle ? { demo_title: trimmedDemoTitle } : {}),
                    },
                };
            },
        }),
        postPortalLabCompanyPortalProcessingStep2: builder.mutation<
            PortalLabCompanyPortalStateResponse,
            PortalLabCompanyPortalProcessingStep2Args
        >({
            query: ({ processingId, body }) => ({
                url: `company-portal/processing/${processingId}/step-2/`,
                method: "POST",
                body,
            }),
        }),
        postPortalLabCompanyPortalProcessingStep3: builder.mutation<
            PortalLabCompanyPortalStateResponse,
            PortalLabCompanyPortalStateId
        >({
            query: (processingId) => ({
                url: `company-portal/processing/${processingId}/step-3/`,
                method: "POST",
            }),
        }),
        postPortalLabCompanyPortalCopilot: builder.mutation<
            PortalLabCompanyPortalCopilotResponse,
            PortalLabCompanyPortalCopilotArgs
        >({
            query: ({ processingId, prompt }) => ({
                url: `company-portal/processing/${processingId}/copilot/`,
                method: "POST",
                body: { prompt },
            }),
        }),
        postPortalLabCompanyPortalGenerateTemplate: builder.mutation<
            Record<string, unknown>,
            PortalLabCompanyPortalGenerateTemplateArgs
        >({
            query: ({ processingId }) => ({
                url: `company-portal/processing/${processingId}/generate-template/`,
                method: "POST",
            }),
        }),
        postPortalLabCompanyPortalProcessingUpload: builder.mutation<
            PortalLabCompanyPortalProcessingUploadResponse,
            PortalLabCompanyPortalProcessingUploadArgs
        >({
            query: ({ file, type }) => {
                const body = new FormData();
                body.append("file", file);
                body.append("type", type);
                return {
                    url: "company-portal/processing/upload/",
                    method: "POST",
                    body,
                };
            },
        }),
        updatePortalLabCompanyPortalState: builder.mutation<
            PortalLabCompanyPortalStateResponse,
            PortalLabUpdateCompanyPortalStateArgs
        >({
            query: ({ processingId, body }) => ({
                url: `demo-lab/${processingId}/company-portal/state/`,
                method: "PUT",
                body,
            }),
        }),
        postPortalLabThoughtspotLiveboardTiles: builder.mutation<
            PortalLabThoughtspotLiveboardTilesByPipelineResponse,
            PortalLabThoughtspotLiveboardTilesByPipelineArgs
        >({
            query: ({ pipeline_id }) => ({
                url: "demo-lab/thoughtspot-liveboards/",
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        postPortalLabThoughtspotLiveboardTilesByConnection: builder.mutation<
            PortalLabThoughtspotLiveboardTilesByConnectionResponse,
            PortalLabThoughtspotLiveboardTilesByConnectionArgs
        >({
            query: (body) => ({
                url: "demo-lab/thoughtspot-liveboard-tiles/",
                method: "POST",
                body,
            }),
        }),
        postPortalLabThoughtspotSpotterModels: builder.mutation<
            PortalLabThoughtspotSpotterModelsResponse,
            PortalLabThoughtspotSpotterModelsArgs
        >({
            query: ({ pipeline_id }) => ({
                url: "demo-lab/thoughtspot-spotter-models/",
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        postPortalLabDatabricksDashboards: builder.mutation<
            PortalLabDatabricksDashboardsResponse,
            PortalLabDatabricksDashboardsArgs
        >({
            query: ({ pipeline_id}) => ({
                url: "demo-lab/databricks-dashboards/",
                method: "POST",
                body: { pipeline_id },
            }),
        }),
        postPortalLabDatabricksDashboardWidgets: builder.mutation<
            PortalLabDatabricksDashboardWidgetsResponse,
            PortalLabDatabricksDashboardWidgetsArgs
        >({
            query: ({ pipeline_id, dashboard_id }) => ({
                url: "demo-lab/databricks-dashboard-widgets/",
                method: "POST",
                body: { pipeline_id, dashboard_id },
            }),
        }),
        postPortalLabDatabricksGenieSpaces: builder.mutation<
            PortalLabDatabricksDashboardWidgetsResponse,
            PortalLabDatabricksDashboardWidgetsArgs
        >({
            query: ({ pipeline_id, dashboard_id }) => ({
                url: "demo-lab/databricks-genie-spaces/",
                method: "POST",
                body: { pipeline_id, dashboard_id },
            }),
        }),
    }),
});

export const portalLabCompanyPortalProcessingStageEndpoint =
    portalLabEndpoints.endpoints.getPortalLabCompanyPortalProcessingStage;

export const upsertPortalLabCompanyPortalProcessingStageCache = (
    processingId: PortalLabCompanyPortalStateId,
    data: PortalLabCompanyPortalStageResponse
) =>
    portalLabEndpoints.util.upsertQueryData(
        "getPortalLabCompanyPortalProcessingStage",
        processingId,
        data
    );

export const portalLabApi = {
    useGetBrandKitMutation: dashboardApi.endpoints.getBrandKit.useMutation,
    useUploadLogoMutation: portalLabEndpoints.endpoints.uploadCompanyPortalLogo.useMutation,
    useGetRecentPortalsQuery: portalLabEndpoints.endpoints.getPortalLabRecentPortals.useQuery,
    useGetCompanyPortalStateQuery: portalLabEndpoints.endpoints.getPortalLabCompanyPortalState.useQuery,
    useLazyGetCompanyPortalStateQuery: portalLabEndpoints.endpoints.getPortalLabCompanyPortalState.useLazyQuery,
    useUpdateCompanyPortalStateMutation: portalLabEndpoints.endpoints.updatePortalLabCompanyPortalState.useMutation,
    useGetPortalLabPageTemplatesQuery: portalLabEndpoints.endpoints.getPortalLabPageTemplates.useQuery,
    useLazyGetPortalLabPageTemplatesQuery: portalLabEndpoints.endpoints.getPortalLabPageTemplates.useLazyQuery,
};

export const {
    useGetBrandKitMutation: usePortalLabGetBrandKitMutation,
    useUploadLogoMutation: usePortalLabUploadLogoMutation,
    useUpdateCompanyPortalStateMutation,
} = portalLabApi;

export const {
    useExtractPortalBrandKitMutation: usePortalLabExtractBrandKitMutation,
    useGetPortalLabRecentPortalsQuery: usePortalLabGetRecentPortalsQuery,
    useDeletePortalLabEntityMutation: usePortalLabDeleteEntityMutation,
    useGetPortalLabCompanyPortalStateQuery: usePortalLabGetCompanyPortalStateQuery,
    useLazyGetPortalLabCompanyPortalStateQuery: useLazyPortalLabGetCompanyPortalStateQuery,
    usePostPortalLabCompanyPortalProcessingStep1Mutation: usePortalLabPostCompanyPortalProcessingStep1Mutation,
    usePostPortalLabCompanyPortalProcessingStep2Mutation: usePortalLabPostCompanyPortalProcessingStep2Mutation,
    usePostPortalLabCompanyPortalProcessingStep3Mutation: usePortalLabPostCompanyPortalProcessingStep3Mutation,
    usePostPortalLabCompanyPortalCopilotMutation: usePortalLabPostCompanyPortalCopilotMutation,
    usePostPortalLabCompanyPortalGenerateTemplateMutation: usePortalLabPostCompanyPortalGenerateTemplateMutation,
    usePostPortalLabCompanyPortalProcessingUploadMutation: usePortalLabPostCompanyPortalProcessingUploadMutation,
    useGetPortalLabCompanyPortalProcessingStageQuery: usePortalLabGetCompanyPortalProcessingStageQuery,
    useLazyGetPortalLabCompanyPortalProcessingStageQuery: useLazyPortalLabGetCompanyPortalProcessingStageQuery,
    useGetPortalLabCompanyPortalProcessingTemplateQuery: usePortalLabGetCompanyPortalProcessingTemplateQuery,
    useLazyGetPortalLabCompanyPortalProcessingTemplateQuery: useLazyPortalLabGetCompanyPortalProcessingTemplateQuery,
    useUpdatePortalLabCompanyPortalStateMutation: usePortalLabUpdateCompanyPortalStateMutation,
    useGetPortalLabPageTemplatesQuery,
    useLazyGetPortalLabPageTemplatesQuery,
    useGetPortalLabCompanyPortalColorThemesQuery,
    useLazyGetPortalLabCompanyPortalColorThemesQuery,
    useGetPortalLabPipelinesListQuery: usePortalLabGetPipelinesListQuery,
    useLazyGetPortalLabPipelinesListQuery: useLazyPortalLabGetPipelinesListQuery,
    usePostPortalLabThoughtspotLiveboardTilesMutation: usePortalLabPostThoughtspotLiveboardTilesMutation,
    usePostPortalLabThoughtspotLiveboardTilesByConnectionMutation: usePortalLabPostThoughtspotLiveboardTilesByConnectionMutation,
    usePostPortalLabThoughtspotSpotterModelsMutation: usePortalLabPostThoughtspotSpotterModelsMutation,
    usePostPortalLabDatabricksDashboardsMutation,
    usePostPortalLabDatabricksDashboardWidgetsMutation,
    usePostPortalLabDatabricksGenieSpacesMutation,
} = portalLabEndpoints;
